/**
 * Global Request Layer with Reliability Features
 * 
 * Features:
 * - Consistent timeout + retry policy for GETs only
 * - Circuit breaker pattern for degraded backend
 * - Request deduplication
 * - Response caching
 * - Runtime host validation (blocks wrong-host calls)
 * 
 * Usage:
 *   import { apiClient } from './utils/apiClient';
 *   const leads = await apiClient.get('/api/leads');
 */

import { getBackendUrl } from './api';

// PRODUCTION HOST VALIDATION
// In production, ALL API calls must use the same origin
const validateRequestHost = (url) => {
  if (typeof window === 'undefined') return true;
  
  const currentHost = window.location.hostname;
  
  // Skip validation for localhost development
  if (currentHost === 'localhost' || currentHost === '127.0.0.1') {
    return true;
  }
  
  try {
    const requestUrl = new URL(url);
    const requestHost = requestUrl.hostname;
    
    // In production, request host MUST match current page host
    if (requestHost !== currentHost) {
      console.error(`🚫 [API BLOCKED] Wrong host detected!`);
      console.error(`   Current page: ${currentHost}`);
      console.error(`   Request URL: ${requestHost}`);
      console.error(`   Expected: ${currentHost}`);
      console.error(`   This request has been blocked to prevent CORS errors.`);
      return false;
    }
  } catch (e) {
    // Relative URLs are fine
    return true;
  }
  
  return true;
};

// Circuit breaker state
let circuitState = {
  isOpen: false,
  failureCount: 0,
  lastFailureTime: 0,
  successCount: 0,
};

// Configuration
const CONFIG = {
  timeout: 30000,           // 30 second timeout
  retryCount: 2,            // Retry GET requests up to 2 times
  retryDelay: 1000,         // 1 second between retries
  circuitThreshold: 5,      // Open circuit after 5 consecutive failures
  circuitResetTime: 60000,  // Try again after 60 seconds
  backoffMultiplier: 2,     // Exponential backoff multiplier
};

// Event listeners for circuit breaker state
const listeners = new Set();

export const onCircuitStateChange = (callback) => {
  listeners.add(callback);
  return () => listeners.delete(callback);
};

const notifyListeners = () => {
  listeners.forEach(cb => cb({ ...circuitState }));
};

// In-flight request deduplication
const pendingRequests = new Map();

/**
 * Check if circuit breaker should allow request
 */
const shouldAllowRequest = () => {
  if (!circuitState.isOpen) return true;
  
  // Check if enough time has passed to try again
  const timeSinceFailure = Date.now() - circuitState.lastFailureTime;
  if (timeSinceFailure > CONFIG.circuitResetTime) {
    console.log('[Circuit Breaker] Half-open, allowing test request');
    return true;
  }
  
  return false;
};

/**
 * Record a successful request
 */
const recordSuccess = () => {
  circuitState.successCount++;
  
  if (circuitState.isOpen && circuitState.successCount >= 2) {
    console.log('[Circuit Breaker] Closing circuit after successful requests');
    circuitState.isOpen = false;
    circuitState.failureCount = 0;
    circuitState.successCount = 0;
    notifyListeners();
  }
};

/**
 * Record a failed request
 */
const recordFailure = () => {
  circuitState.failureCount++;
  circuitState.successCount = 0;
  circuitState.lastFailureTime = Date.now();
  
  if (circuitState.failureCount >= CONFIG.circuitThreshold && !circuitState.isOpen) {
    console.warn('[Circuit Breaker] Opening circuit due to consecutive failures');
    circuitState.isOpen = true;
    notifyListeners();
  }
};

/**
 * Sleep for specified milliseconds
 */
const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Make a fetch request with timeout
 */
const fetchWithTimeout = async (url, options, timeout) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);
  
  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
    });
    clearTimeout(timeoutId);
    return response;
  } catch (error) {
    clearTimeout(timeoutId);
    throw error;
  }
};

/**
 * API Client with retry and circuit breaker
 */
export const apiClient = {
  // Use dynamic getter - NEVER cache the baseURL
  get baseURL() {
    return getBackendUrl();
  },
  
  /**
   * GET request with retry
   */
  async get(path, options = {}) {
    const url = `${this.baseURL}${path}`;
    
    // PRODUCTION SAFETY: Block wrong-host requests
    if (!validateRequestHost(url)) {
      throw new Error(`Request blocked: wrong host. Expected ${window.location.hostname}`);
    }
    
    const cacheKey = `GET:${url}`;
    
    // Circuit breaker check
    if (!shouldAllowRequest()) {
      throw new Error('Service temporarily unavailable (circuit breaker open)');
    }
    
    // Request deduplication
    if (pendingRequests.has(cacheKey)) {
      return pendingRequests.get(cacheKey);
    }
    
    const requestPromise = this._fetchWithRetry(url, {
      method: 'GET',
      ...options,
    });
    
    pendingRequests.set(cacheKey, requestPromise);
    
    try {
      const result = await requestPromise;
      recordSuccess();
      return result;
    } catch (error) {
      recordFailure();
      throw error;
    } finally {
      pendingRequests.delete(cacheKey);
    }
  },
  
  /**
   * POST request (no retry for mutations)
   */
  async post(path, data, options = {}) {
    const url = `${this.baseURL}${path}`;
    
    // PRODUCTION SAFETY: Block wrong-host requests
    if (!validateRequestHost(url)) {
      throw new Error(`Request blocked: wrong host. Expected ${window.location.hostname}`);
    }
    
    if (!shouldAllowRequest()) {
      throw new Error('Service temporarily unavailable (circuit breaker open)');
    }
    
    try {
      const response = await fetchWithTimeout(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...options.headers,
        },
        body: JSON.stringify(data),
      }, options.timeout || CONFIG.timeout);
      
      if (response.ok) {
        recordSuccess();
      } else if (response.status >= 500) {
        recordFailure();
      }
      
      return response;
    } catch (error) {
      recordFailure();
      throw error;
    }
  },
  
  /**
   * Internal fetch with retry logic
   */
  async _fetchWithRetry(url, options, retryCount = CONFIG.retryCount) {
    let lastError;
    
    for (let attempt = 0; attempt <= retryCount; attempt++) {
      try {
        if (attempt > 0) {
          const delay = CONFIG.retryDelay * Math.pow(CONFIG.backoffMultiplier, attempt - 1);
          console.log(`[API] Retry ${attempt}/${retryCount} for ${url} after ${delay}ms`);
          await sleep(delay);
        }
        
        const response = await fetchWithTimeout(url, options, options.timeout || CONFIG.timeout);
        
        // Don't retry on 4xx errors (client errors)
        if (response.status >= 400 && response.status < 500) {
          return response;
        }
        
        // Retry on 5xx errors
        if (response.status >= 500 && attempt < retryCount) {
          lastError = new Error(`HTTP ${response.status}`);
          continue;
        }
        
        return response;
      } catch (error) {
        lastError = error;
        
        // Don't retry on abort (timeout)
        if (error.name === 'AbortError') {
          throw new Error(`Request timeout after ${CONFIG.timeout}ms`);
        }
        
        if (attempt === retryCount) {
          throw error;
        }
      }
    }
    
    throw lastError;
  },
  
  /**
   * Get circuit breaker state
   */
  getCircuitState() {
    return { ...circuitState };
  },
  
  /**
   * Reset circuit breaker (for testing)
   */
  resetCircuit() {
    circuitState = {
      isOpen: false,
      failureCount: 0,
      lastFailureTime: 0,
      successCount: 0,
    };
    notifyListeners();
  },
};

export default apiClient;
